import React, { createContext, useContext, useState } from 'react';

// The admin dashboard's navigation and its building blocks (IDD D-HP-ADMIN-040): a sidebar that lists every page
// grouped by what it operates, with the sections of the open page as an indented list under it; sections of a page
// with a header that carries the state and the count, collapsed when they hold reference material or a form; a
// sub-navigation for a page that is split into sub-pages; the page head; and "show more" for long lists. The place
// is kept in the address (`#page/section` and, for a sub-page or a focused item, `#page/section/detail`), so a
// reload, the browser's back button and a copied link return to the same place. A badge counts what needs a person.

export const readAdminRoute = () => {
  const [tab = '', section = '', detail = ''] = String(window.location.hash || '').replace(/^#/, '').split('/');
  return { tab: decodeURIComponent(tab), section: decodeURIComponent(section), detail: decodeURIComponent(detail) };
};

export const writeAdminRoute = (tab, section, detail = '') => {
  const next = `#${encodeURIComponent(tab)}${section ? `/${encodeURIComponent(section)}` : ''}${section && detail ? `/${encodeURIComponent(detail)}` : ''}`;
  if (window.location.hash !== next) window.location.hash = next;
};

// What a viewer chose is remembered in the browser only: an open or closed section, the sub-page of a page. The
// storage may be absent or refused; then the default stands.
const readStored = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
};
const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // The browser keeps no choice; the default stands on the next visit.
  }
};
export const readSubPage = (page, fallback) => readStored(`admin.subpage.${page}`, fallback);
export const writeSubPage = (page, value) => writeStored(`admin.subpage.${page}`, value);
export const readAdminStored = (key, fallback = '') => readStored(`admin.${key}`, fallback);
export const writeAdminStored = (key, value) => writeStored(`admin.${key}`, value);

// The labels the building blocks need, handed down once by the dashboard page; a stand-alone page runs without them.
export const AdminLabelsContext = createContext({});
export const useAdminLabels = () => useContext(AdminLabelsContext) || {};

const Badge = ({ badge }) => (badge && badge.count ? (
  <span className={`admin-nav-badge ${badge.tone === 'error' ? 'is-error' : 'is-warning'}`}>{badge.count}</span>
) : null);

// The sidebar: the pages in their groups, which follow the working day of the company (IDD D-HP-ADMIN-040), the open
// page marked and its sections listed under it. A group that holds one page with several sections (`flat`) lists those
// sections as its entries, so the person reads six sub-pages of the organisation, not a page that repeats the group's name.
export const AdminSideNav = ({ title, kicker = '', groups, active, activeSection = '', sectionGroups = [], onSelectPage, onSelectSection, badges = {}, sectionBadges = {} }) => (
  <nav className="admin-sidebar" aria-label={title}>
    <div className="admin-sidebar-head">
      {kicker ? <div className="editorial-kicker">{kicker}</div> : null}
      <div className="admin-sidebar-title">{title}</div>
    </div>
    {groups.map((group) => (
      <div key={group.id} className="admin-sidebar-group">
        <div className="admin-sidebar-group-label">{group.label}</div>
        <ul className="admin-sidebar-list">
          {group.flat ? (group.flat.sections || []).map((section) => (
            <li key={section.id}>
              <button
                type="button"
                className={`admin-sidebar-link ${active === group.flat.tab && activeSection === section.id ? 'active' : ''}`}
                aria-current={active === group.flat.tab && activeSection === section.id ? 'page' : undefined}
                onClick={() => onSelectSection(section.id, group.flat.tab)}
              >
                <span>{section.label}</span>
                <Badge badge={active === group.flat.tab ? sectionBadges[section.id] : (badges[`${group.flat.tab}/${section.id}`] || null)} />
              </button>
            </li>
          )) : null}
          {!group.flat ? group.tabs.map((tab) => (
            <li key={tab.id}>
              <button
                type="button"
                className={`admin-sidebar-link ${active === tab.id ? 'active' : ''}`}
                aria-current={active === tab.id ? 'page' : undefined}
                onClick={() => onSelectPage(tab.id)}
              >
                <span>{tab.label}</span>
                <Badge badge={badges[tab.id]} />
              </button>
              {active === tab.id && sectionGroups.length ? sectionGroups.map((sections, index) => (
                <ul key={index} className={`admin-sidebar-sublist ${index ? 'is-next-group' : ''}`}>
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        type="button"
                        className={`admin-sidebar-sublink ${activeSection === section.id ? 'active' : ''}`}
                        aria-current={activeSection === section.id ? 'page' : undefined}
                        onClick={() => onSelectSection(section.id)}
                      >
                        <span>{section.label}</span>
                        <Badge badge={sectionBadges[section.id]} />
                      </button>
                    </li>
                  ))}
                </ul>
              )) : null}
            </li>
          )) : null}
        </ul>
      </div>
    ))}
  </nav>
);

// A section's open or closed state, remembered per section while it has an id.
export const useSectionOpen = (id, fallback = true) => {
  const [open, setOpen] = useState(() => {
    if (!id) return fallback;
    const stored = readStored(`admin.section.${id}`, '');
    return stored === '' ? fallback : stored === '1';
  });
  const toggle = () => setOpen((current) => {
    const next = !current;
    if (id) writeStored(`admin.section.${id}`, next ? '1' : '0');
    return next;
  });
  return [open, toggle];
};

const toneClass = (tone) => (tone === 'error' ? 'is-missing' : tone === 'warning' ? 'is-warning' : tone === 'ok' ? 'is-ok' : 'is-neutral');

// One section of a page: a header with the title, the state in words, the count as a pill and the actions; the
// body below. `collapsed` is the default for reference material and forms; the viewer's choice is remembered under
// the section's id. A closed section keeps its content mounted, so a form draft, a filter or a running call survives.
export const AdminSection = ({ id = '', title, subtitle = '', state = '', count = null, tone = '', collapsed = false, actions = null, className = '', children }) => {
  const [open, toggle] = useSectionOpen(id, !collapsed);
  const labels = useAdminLabels();
  return (
    <section id={id || undefined} className={`site-card admin-panel admin-section ${open ? 'is-open' : 'is-closed'} ${className}`}>
      <div className="admin-section-head">
        <div className="admin-section-heading">
          <button type="button" className="admin-section-toggle" aria-expanded={open} onClick={toggle} title={open ? labels.collapse : labels.expand}>
            <span className="admin-section-caret" aria-hidden="true" />
            <h2>{title}</h2>
          </button>
          {state ? <span className="admin-section-state">{state}</span> : null}
          {count !== null && count !== undefined ? <span className={`admin-status-pill admin-section-count ${toneClass(tone)}`}>{count}</span> : null}
        </div>
        {actions && open ? <div className="admin-section-actions">{actions}</div> : null}
      </div>
      {subtitle && open ? <p className="site-copy admin-section-subtitle">{subtitle}</p> : null}
      <div className="admin-section-body" hidden={!open}>{children}</div>
    </section>
  );
};

// The former card of a tab; kept as a name for the section.
export const AdminCard = AdminSection;

// The head of a page: its title, one sentence of what it operates, the facts of its state on the right, and the
// buttons that lead elsewhere.
export const AdminPageHead = ({ title, subtitle = '', meta = [], actions = null, children }) => {
  const facts = meta.filter((entry) => Array.isArray(entry) && entry[0]);
  return (
    <header className="site-card admin-panel admin-page-head">
      <div className="admin-section-head">
        <div className="admin-page-head-text">
          <h1 className="admin-hero-title">{title}</h1>
          {subtitle ? <p className="site-copy">{subtitle}</p> : null}
        </div>
        {facts.length ? (
          <dl className="admin-page-meta">
            {facts.map(([label, value]) => (
              <div key={label}><dt>{label}</dt><dd>{value === null || value === undefined || value === '' ? '-' : value}</dd></div>
            ))}
          </dl>
        ) : null}
      </div>
      {actions ? <div className="admin-page-actions">{actions}</div> : null}
      {children}
    </header>
  );
};

// A dialog over the page (IDD D-HP-ADMIN-041): fixed and centred on a dimmed backdrop, the keyboard focus moved into
// it and given back on close, the page behind unchanged. Escape and a click on the backdrop ask the owner of the dialog
// to close it (`onRequestClose`), which may confirm first; the dialog itself closes nothing.
export const AdminDialog = ({ title, subtitle = '', onRequestClose, className = '', labels = {}, children }) => {
  const box = React.useRef(null);
  React.useEffect(() => {
    const before = document.activeElement;
    const node = box.current;
    if (node) {
      const first = node.querySelector('textarea, input, select, button');
      (first || node).focus({ preventScroll: true });
    }
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onRequestClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (before && typeof before.focus === 'function') before.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="org-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onRequestClose(); }}>
      <div ref={box} className={`org-dialog ${className}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div className="org-dialog__head">
          <div>
            <h3 className="org-dialog__title">{title}</h3>
            {subtitle ? <div className="org-dialog__subtitle">{subtitle}</div> : null}
          </div>
          <button type="button" className="org-dialog__close" onClick={onRequestClose} title={labels.close} aria-label={labels.close}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

// A long list shows its newest rows and offers the next step on request, so no page grows past the screen's patience.
export const ShowMore = ({ shown, total, onMore }) => {
  const labels = useAdminLabels();
  if (!(total > shown)) return null;
  return (
    <div className="admin-show-more">
      <span className="admin-show-more-count">{fill(labels.shownOf, { shown, total })}</span>
      <button type="button" className="homepage-button-secondary" onClick={onMore}>{labels.showMore}</button>
    </div>
  );
};

export const useShowMore = (step = 50) => {
  const [shown, setShown] = useState(step);
  return [shown, () => setShown((current) => current + step)];
};

export const fill = (text, values) => Object.entries(values || {}).reduce(
  (result, [key, value]) => result.split(`{${key}}`).join(String(value ?? '-')),
  String(text || ''),
);
